// Client-side background removal for battle card portraits.
// Uses @imgly/background-removal (ONNX Runtime Web) — runs entirely in the
// browser, model weights cached by the browser after first load.
//
// Results are memoised per source URL in an in-memory Map so switching
// cards / retaking photos doesn't re-run the model.

let _removeBg = null;    // lazy-loaded function reference
let _loading = null;     // in-flight import promise
const cache = new Map(); // sourceUrl -> objectUrl (transparent PNG)
const inflight = new Map(); // sourceUrl -> Promise<objectUrl>

async function ensureLoaded() {
  if (_removeBg) return _removeBg;
  if (!_loading) {
    _loading = import('@imgly/background-removal').then(mod => {
      _removeBg = mod.removeBackground || mod.default;
      return _removeBg;
    });
  }
  return _loading;
}

/** Convert a same-origin URL to a Blob so we sidestep any CORS quirks. */
async function fetchAsBlob(url) {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
  return await res.blob();
}

/**
 * Remove the background from a card image URL and return an object-URL
 * for the transparent PNG. Cached per source URL.
 *
 * @param {string} url - The source image URL (card portrait).
 * @param {(progress:number)=>void} [onProgress] - 0..1 progress reporter.
 * @returns {Promise<string>} objectURL for the transparent PNG.
 */
export async function getCardCutout(url, onProgress) {
  if (!url) throw new Error('No image URL');
  const key = url;
  if (cache.has(key)) return cache.get(key);
  if (inflight.has(key)) return inflight.get(key);

  const task = (async () => {
    const removeBg = await ensureLoaded();
    // Pass the URL string directly — @imgly/background-removal handles the
    // fetch itself, which is more tolerant of CORS variations for hosted
    // wildlife photos (iNaturalist S3, /api/files, etc.). If that fails
    // (rare), fall back to a same-origin fetch → Blob approach.
    let input = url;
    try {
      const outBlob = await removeBg(input, {
        progress: (_key, current, total) => {
          if (typeof onProgress === 'function' && total > 0) {
            onProgress(Math.min(1, current / total));
          }
        },
        output: { format: 'image/png', quality: 0.9 },
      });
      const objectUrl = URL.createObjectURL(outBlob);
      cache.set(key, objectUrl);
      return objectUrl;
    } catch {
      // Retry via explicit blob for same-origin cases where the direct
      // URL path stumbles on credentialed responses.
      input = await fetchAsBlob(url);
      const outBlob = await removeBg(input, {
        output: { format: 'image/png', quality: 0.9 },
      });
      const objectUrl = URL.createObjectURL(outBlob);
      cache.set(key, objectUrl);
      return objectUrl;
    }
  })();
  inflight.set(key, task);
  try {
    return await task;
  } finally {
    inflight.delete(key);
  }
}
